import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from '@ublique/components/LoadingModal'
import { sendNotification } from 'pages/Administration/helpers'
import { filter, first, isEmpty } from 'lodash'
import CreationForm from 'pages/Administration/Tabs/Output/OutputPanel/CreationForm'
import { addOutputPermission } from 'pages/Administration/Tabs/Output/OutputPanel/utilities/actions'
import useStoreSelector from 'utils/hooks/useStoreSelector'
import { outputRoutesSelector } from 'pages/Administration/Tabs/Output/OutputPanel/utilities/helpers'
import Loading from '@ublique/components/Loading'
import 'pages/Administration/components/index.scss'

const OutputModal = ({ open, close, data }) => {
  const { modalType, outputValue, roles } = data
  const allOutput = useStoreSelector('appConfig', outputRoutesSelector)

  const { t } = useTranslation(['Administration', 'Common', 'OutputView'])
  const [isLoading, setIsLoading] = useState(false)

  const findLabel = (outputPage) => {
    const findOutput = filter(allOutput, {
      component: outputPage
    })
    const output = first(findOutput)
    return t(`OutputView:${output?.title}`) + ' - ' + output?.path ?? outputPage
  }

  const getInitialPages = () => {
    if (outputValue?.permissions) {
      return outputValue.permissions
        .filter((permission) => permission.read)
        .map(({ outputField }) => ({
          label: findLabel(outputField),
          value: outputField
        }))
    } else {
      return []
    }
  }
  const [role, setRole] = useState(outputValue?.roleName || null)
  const [pages, setPages] = useState(getInitialPages())

  const [isRoleInvalid, setIsRoleInvalid] = useState(false)
  const [isPagesInvalid, setIsPagesInvalid] = useState(false)

  const processValidation = (type, value) => {
    if (type === 'role') setIsRoleInvalid(isEmpty(value))
    if (type === 'pages') setIsPagesInvalid(isEmpty(value))
    return isEmpty(value)
  }

  const onChange = useCallback(
    (type, value) => {
      if (type === 'role') {
        setRole(value)
        processValidation('role', value)
      }
      if (type === 'pages') {
        setPages(value)
        processValidation('pages', value)
      }
    },
    [role, pages]
  )

  const onSubmit = async ({ role, pages }) => {
    addOutputPermission({ role, pages })
      .then(() => {
        sendNotification({ t, info: modalType, kind: 'success' })
        close()
      })
      .catch(() => {
        sendNotification({ t, info: modalType, kind: 'error' })
      })
  }

  const handleSubmit = useCallback(() => {
    const ri = processValidation('role', role)
    const pi = processValidation('pages', pages)
    if (ri || pi) return
    setIsLoading(true)
    onSubmit({ role, pages })
    setIsLoading(false)
  }, [role, pages])

  return (
    <Modal
      t={t}
      open={open}
      onRequestSubmit={handleSubmit}
      onRequestClose={close}
      onSecondarySubmit={close}
      className='admin-view'
      primaryButtonText={t('Common:Save')}
      secondaryButtonText={t('Common:Cancel')}
      modalHeading={t('OutputManagement.OutputPermissions')}
      primaryButtonDisabled={isRoleInvalid || isPagesInvalid}
      preventCloseOnClickOutside
      children={
        <>
          {isLoading && <Loading description='Loading' withOverlay />}
          <CreationForm
            t={t}
            rolesList={roles}
            role={role}
            pages={pages}
            pagesList={allOutput}
            onChange={onChange}
            isRoleInvalid={isRoleInvalid}
            isPagesInvalid={isPagesInvalid}
          />
        </>
      }
    />
  )
}

export default OutputModal
